import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Eye, DollarSign } from 'lucide-react';

// Public deployments use the same origin for the UI and API. Local development
// keeps the configured backend URL so the existing localhost workflow still works.
const BACKEND_URL = (typeof window !== 'undefined' &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1' &&
  !window.location.hostname.includes('preview.emergentagent.com'))
  ? ''
  : (process.env.REACT_APP_BACKEND_URL || '');
const API = `${BACKEND_URL}/api`;

const SalaryPayments = ({ currentUser }) => {
  const [employees, setEmployees] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNewPaymentDialogOpen, setIsNewPaymentDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '',
    work_days: 30,
    absent_days: 0,
    late_hours: 0,
    payment_method: 'kasa',
    notes: ''
  });

  useEffect(() => {
    fetchEmployees();
    fetchSalaryPayments();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Personeller yüklenirken hata:", error);
      toast.error("Personeller yüklenirken hata oluştu");
    }
  };

  const fetchSalaryPayments = async () => {
    try {
      const response = await axios.get(`${API}/salary-payments`);
      setSalaryPayments(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Maaş ödemeleri yüklenirken hata:", error);
      toast.error("Maaş ödemeleri yüklenirken hata oluştu");
      setLoading(false);
    }
  };

  const handleNewPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/salary-payments`, {
        ...formData,
        work_days: parseInt(formData.work_days),
        absent_days: parseInt(formData.absent_days),
        late_hours: parseFloat(formData.late_hours)
      });
      
      toast.success("Maaş ödemesi başarıyla kaydedildi!");
      setIsNewPaymentDialogOpen(false);
      setFormData({
        employee_id: '',
        work_days: 30,
        absent_days: 0,
        late_hours: 0,
        payment_method: 'kasa',
        notes: ''
      });
      fetchSalaryPayments();
    } catch (error) {
      console.error("Maaş ödemesi kaydedilirken hata:", error);
      toast.error("Maaş ödemesi kaydedilirken hata oluştu");
    }
  };

  const handleViewHistory = async (employee) => {
    try {
      const response = await axios.get(`${API}/salary-payments/employee/${employee.id}`);
      setSelectedEmployee(employee);
      setEmployeeHistory(response.data);
      setIsHistoryDialogOpen(true);
    } catch (error) {
      console.error("Personel maaş geçmişi yüklenirken hata:", error);
      toast.error("Personel maaş geçmişi yüklenirken hata oluştu");
    }
  };

  const calculateSalaryPreview = () => {
    const employee = employees.find(emp => emp.id === formData.employee_id);
    if (!employee) return null;

    const baseSalary = employee.monthly_salary;
    const dailySalary = baseSalary / 30;
    const hourlySalary = dailySalary / 8;
    
    const absentDeduction = formData.absent_days * dailySalary;
    const lateDeduction = formData.late_hours * hourlySalary;
    const finalAmount = baseSalary - absentDeduction - lateDeduction;

    return {
      baseSalary,
      dailySalary,
      hourlySalary,
      absentDeduction,
      lateDeduction,
      finalAmount
    };
  };

  const salaryPreview = calculateSalaryPreview();

  if (loading) {
    return <div className="flex justify-center items-center h-96">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Personel Maaşları</h1>
          <p className="text-muted-foreground">Personel maaş ödemelerini yönetin</p>
        </div>
        <Button onClick={() => setIsNewPaymentDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Maaş Ödemesi Yap
        </Button>
      </div>

      {/* Personel Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.map((employee) => {
          const employeePayments = salaryPayments.filter(payment => payment.employee_id === employee.id);
          const totalPaid = employeePayments.reduce((sum, payment) => sum + payment.final_amount, 0);
          const lastPayment = employeePayments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))[0];

          return (
            <Card key={employee.id}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{employee.name} {employee.surname}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Başlangıç: {new Date(employee.start_date).toLocaleDateString('tr-TR')}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Aylık Maaş:</span>
                    <span className="font-medium">₺{employee.monthly_salary.toLocaleString('tr-TR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Toplam Ödenen:</span>
                    <span className="font-medium text-green-600">₺{totalPaid.toLocaleString('tr-TR')}</span>
                  </div>
                  {lastPayment && (
                    <div className="flex justify-between">
                      <span className="text-sm">Son Ödeme:</span>
                      <span className="text-xs">{new Date(lastPayment.payment_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                  <div className="flex space-x-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewHistory(employee)}
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Geçmiş
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Son Maaş Ödemeleri */}
      <Card>
        <CardHeader>
          <CardTitle>Son Maaş Ödemeleri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Personel</th>
                  <th className="text-left p-2">Temel Maaş</th>
                  <th className="text-left p-2">Devamsızlık</th>
                  <th className="text-left p-2">Geç Kalma</th>
                  <th className="text-left p-2">Net Ödeme</th>
                  <th className="text-left p-2">Ödeme Yöntemi</th>
                  <th className="text-left p-2">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {salaryPayments.slice(0, 10).map((payment) => (
                  <tr key={payment.id} className="border-b">
                    <td className="p-2">{payment.employee_name}</td>
                    <td className="p-2">₺{payment.base_salary.toLocaleString('tr-TR')}</td>
                    <td className="p-2">
                      {payment.absent_days > 0 && (
                        <Badge variant="destructive">{payment.absent_days} gün</Badge>
                      )}
                    </td>
                    <td className="p-2">
                      {payment.late_hours > 0 && (
                        <Badge variant="destructive">{payment.late_hours} saat</Badge>
                      )}
                    </td>
                    <td className="p-2 font-medium text-green-600">
                      ₺{payment.final_amount.toLocaleString('tr-TR')}
                    </td>
                    <td className="p-2">
                      <Badge variant={payment.payment_method === 'kasa' ? 'default' : 'secondary'}>
                        {payment.payment_method === 'kasa' ? 'Kasa' : 'Banka'}
                      </Badge>
                    </td>
                    <td className="p-2">{new Date(payment.payment_date).toLocaleDateString('tr-TR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Yeni Maaş Ödemesi Dialog */}
      <Dialog open={isNewPaymentDialogOpen} onOpenChange={setIsNewPaymentDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Maaş Ödemesi Yap</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNewPayment} className="space-y-4">
            <div>
              <Label htmlFor="employee_id">Personel Seçin</Label>
              <Select value={formData.employee_id} onValueChange={(value) => setFormData({...formData, employee_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name} {employee.surname} - ₺{employee.monthly_salary.toLocaleString('tr-TR')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="work_days">Çalışılan Gün</Label>
                <Input
                  id="work_days"
                  type="number"
                  min="1"
                  max="30"
                  value={formData.work_days}
                  onChange={(e) => setFormData({...formData, work_days: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="absent_days">Devamsızlık (Gün)</Label>
                <Input
                  id="absent_days"
                  type="number"
                  min="0"
                  max="30"
                  value={formData.absent_days}
                  onChange={(e) => setFormData({...formData, absent_days: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="late_hours">Geç Kalma (Saat)</Label>
                <Input
                  id="late_hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.late_hours}
                  onChange={(e) => setFormData({...formData, late_hours: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="payment_method">Ödeme Yöntemi</Label>
              <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kasa">Kasa (Nakit)</SelectItem>
                  <SelectItem value="banka">Banka</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">Notlar</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Opsiyonel notlar"
              />
            </div>

            {/* Maaş Hesaplama Önizlemesi */}
            {salaryPreview && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Maaş Hesaplama Önizlemesi</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p>Temel Maaş: ₺{salaryPreview.baseSalary.toLocaleString('tr-TR')}</p>
                    <p>Günlük Maaş: ₺{salaryPreview.dailySalary.toFixed(2)}</p>
                    <p>Saatlik Maaş: ₺{salaryPreview.hourlySalary.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-red-600">Devamsızlık Kesintisi: -₺{salaryPreview.absentDeduction.toFixed(2)}</p>
                    <p className="text-red-600">Geç Kalma Kesintisi: -₺{salaryPreview.lateDeduction.toFixed(2)}</p>
                    <p className="font-medium text-green-600">Net Ödeme: ₺{salaryPreview.finalAmount.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsNewPaymentDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={!formData.employee_id}>
                <DollarSign className="h-4 w-4 mr-2" />
                Ödeme Yap
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Personel Maaş Geçmişi Dialog */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedEmployee && `${selectedEmployee.name} ${selectedEmployee.surname} - Maaş Geçmişi`}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Tarih</th>
                  <th className="text-left p-2">Temel Maaş</th>
                  <th className="text-left p-2">Çalışılan Gün</th>
                  <th className="text-left p-2">Devamsızlık</th>
                  <th className="text-left p-2">Geç Kalma</th>
                  <th className="text-left p-2">Net Ödeme</th>
                  <th className="text-left p-2">Ödeme Yöntemi</th>
                </tr>
              </thead>
              <tbody>
                {employeeHistory.map((payment) => (
                  <tr key={payment.id} className="border-b">
                    <td className="p-2">{new Date(payment.payment_date).toLocaleDateString('tr-TR')}</td>
                    <td className="p-2">₺{payment.base_salary.toLocaleString('tr-TR')}</td>
                    <td className="p-2">{payment.work_days} gün</td>
                    <td className="p-2">
                      {payment.absent_days > 0 ? (
                        <Badge variant="destructive">{payment.absent_days} gün</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-2">
                      {payment.late_hours > 0 ? (
                        <Badge variant="destructive">{payment.late_hours} saat</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-2 font-medium text-green-600">
                      ₺{payment.final_amount.toLocaleString('tr-TR')}
                    </td>
                    <td className="p-2">
                      <Badge variant={payment.payment_method === 'kasa' ? 'default' : 'secondary'}>
                        {payment.payment_method === 'kasa' ? 'Kasa' : 'Banka'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryPayments;
